import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm } from './client'
import type { DashboardCard } from './dashboard'

/**
 * Contractul listelor Proactivitee din /api/v2/proactivitee/*.
 * `devices` pe API: all | workstations | servers | mobiles | network | extended.
 * Slugurile de pagina raman cele din portal (all_devices, network_devices, ...).
 * `alarms` nu e aici: ramane pe `/$door/alarms`. `dashboard` e tabloul CI.
 */

export const LISTS = ['all', 'workstations', 'servers', 'mobiles', 'network', 'extended'] as const
export type ListName = (typeof LISTS)[number]

export function isList(value: string): value is ListName {
  return (LISTS as readonly string[]).includes(value)
}

export const PAGES = [
  'all_devices',
  'workstations',
  'servers',
  'network_devices',
  'mobiles',
  'extended_monitoring',
] as const
export type PageName = (typeof PAGES)[number]

export function isPage(value: string): value is PageName {
  return (PAGES as readonly string[]).includes(value)
}

export function listOf(page: string): ListName {
  if (page === 'all_devices') return 'all'
  if (page === 'network_devices') return 'network'
  if (page === 'extended_monitoring') return 'extended'
  if (page === 'workstations' || page === 'servers' || page === 'mobiles') return page
  return 'all'
}

export interface TableRequest {
  list: ListName
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

export interface TablePage<T> {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: T[]
}

export function tableParams(r: TableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('devices', r.list)
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export function devicesTableQuery(r: TableRequest) {
  return queryOptions({
    queryKey: ['proactivitee', 'devices', r],
    queryFn: () => apiGet<TablePage<Record<string, unknown>>>(`/proactivitee/devices-table?${tableParams(r).toString()}`),
  })
}

export interface HostTreeNode {
  id: number
  name: string
  type: number
  virtual: number
  device_type: string
  host_type: string
  host_type_label: string
  seen: string
  icon?: string
  guests: HostTreeNode[]
}

export interface HostTreeRequest {
  list: ListName
  search?: string
}

export function hostTreeQuery(r: HostTreeRequest) {
  const p = new URLSearchParams()
  p.set('devices', r.list)
  if (r.search) p.set('search[value]', r.search)
  return queryOptions({
    queryKey: ['proactivitee', 'host-tree', r],
    queryFn: () => apiGet<{ roots: HostTreeNode[] }>(`/proactivitee/host-tree?${p.toString()}`),
  })
}

export function deviceRowQuery(id: number) {
  return queryOptions({
    queryKey: ['proactivitee', 'device', id],
    queryFn: () => apiGet<Record<string, unknown>>(`/proactivitee/device/${id}`),
  })
}

export interface SnmpTableRequest {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

function snmpTableParams(r: SnmpTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

/**
 * Tabloul SNMP - ce raspunde `App\Proactivitee\Snmp\SnmpBoard`.
 *
 * Acelasi tip de card ca pe tabloul central si pe cele cinci tablouri de
 * modul (`DashboardCard`): HTML-ul unui card vine gata facut din API, scris
 * de `CardMarkup`. Ce lipseste fata de un tablou de modul sunt perspectivele,
 * clasele de active si ferestrele de timp - ecranul asta n-are niciuna, si
 * `SnmpBoard` spune de ce.
 *
 * `read` e steagul sursei: cand e stins, cifrele n-au putut fi citite, si
 * cardurile poarta semnul intrebarii in loc de zero.
 */
export interface SnmpBoardPage {
  pageTitle: string
  lead: string
  cards: DashboardCard[]
  tally: { read: number; unreadable: number }
  noCompany: boolean
  read: boolean
}

export function snmpDashboardQuery() {
  return queryOptions({
    queryKey: ['proactivitee', 'snmp', 'dashboard'],
    queryFn: () => apiGet<SnmpBoardPage>('/proactivitee/snmp/dashboard'),
  })
}

export function snmpServicesTableQuery(r: SnmpTableRequest) {
  return queryOptions({
    queryKey: ['proactivitee', 'snmp', 'services', r],
    queryFn: () => apiGet<TablePage<Record<string, unknown>> & { canAdd?: boolean }>(`/proactivitee/snmp/services-table?${snmpTableParams(r).toString()}`),
  })
}

export function snmpHostsTableQuery(r: SnmpTableRequest) {
  return queryOptions({
    queryKey: ['proactivitee', 'snmp', 'hosts', r],
    queryFn: () => apiGet<TablePage<Record<string, unknown>> & { canAdd?: boolean }>(`/proactivitee/snmp/hosts-table?${snmpTableParams(r).toString()}`),
  })
}

/** Un detaliu al serviciului pe o masina - o linie din serviceDevices.php. */
export interface SnmpServiceEntry {
  service_details: string
  description: string
  outage_date: string
  recover_details: string
  recover_date: string
  duration: string
}

/** Masina cu detaliile ei, grupate: numele se scrie o data pe grup. */
export interface SnmpServiceDeviceRow {
  device_id: number
  device_name: string
  entries: SnmpServiceEntry[]
}

export function snmpServiceDevicesQuery(id: number, status: number) {
  return queryOptions({
    queryKey: ['proactivitee', 'snmp', 'service', id, status],
    queryFn: () => apiGet<{ data: SnmpServiceDeviceRow[] }>(`/proactivitee/snmp/services/${id}/devices?status=${status}`),
  })
}

export function snmpHostDetailsQuery(id: number) {
  return queryOptions({
    queryKey: ['proactivitee', 'snmp', 'host', id],
    queryFn: () => apiGet<Record<string, unknown>>(`/proactivitee/snmp/hosts/${id}`),
  })
}

export function snmpHostFormQuery() {
  return queryOptions({
    queryKey: ['proactivitee', 'snmp', 'host-form'],
    queryFn: () => apiGet<{ candidates: Record<string, unknown>[]; probes: Record<string, unknown>[]; communities: Record<string, unknown>[] }>('/proactivitee/snmp/hosts/form'),
  })
}

export function snmpChecksFormQuery() {
  return queryOptions({
    queryKey: ['proactivitee', 'snmp', 'checks-form'],
    queryFn: () => apiGet<{ hosts: Record<string, unknown>[]; checks: Record<string, unknown>[] }>('/proactivitee/snmp/checks/form'),
  })
}

export function addSnmpHost(fields: Record<string, string | number>) {
  return apiPost<{ status: string; message: string }>('/proactivitee/snmp/hosts', fields)
}

/**
 * Ce se poate regla la fiecare verificare bifata (Proactivitee::externalCheckFields):
 * numele campului, implicita din sablon ca sugestie, si daca implicita difera
 * intre tipurile de masini.
 */
export type SnmpCheckField = { field: string; suggestion: string; varies: boolean }

export function snmpCheckFields(checks: number[]) {
  const q = new URLSearchParams()
  for (const id of checks) q.append('checks[]', String(id))
  return apiGet<{ fields: Record<string, SnmpCheckField[]> }>(`/proactivitee/snmp/checks/fields?${q.toString()}`)
}

/**
 * `values[serviceId][field]` - numai ce a scris omul; o casuta goala inseamna
 * "fiecare masina isi ia implicita ei din sablon", nu "sterge parametrul".
 */
export function addSnmpChecks(devices: number[], checks: number[], values: Record<string, Record<string, string>> = {}) {
  const body = new FormData()
  for (const id of devices) body.append('devices[]', String(id))
  for (const id of checks) body.append('checks[]', String(id))
  for (const [serviceId, fields] of Object.entries(values)) {
    for (const [field, value] of Object.entries(fields)) {
      if (value.trim() !== '') body.append(`values[${serviceId}][${field}]`, value)
    }
  }
  return apiPostForm<{ status: string; message: string }>('/proactivitee/snmp/checks', body)
}

export function coreApplicationsPageQuery() {
  return queryOptions({
    queryKey: ['proactivitee', 'core-apps', 'page'],
    queryFn: () => apiGet<Record<string, unknown>>('/proactivitee/core-applications'),
  })
}

export function coreApplicationsTableQuery(r: SnmpTableRequest) {
  return queryOptions({
    queryKey: ['proactivitee', 'core-apps', 'table', r],
    queryFn: () => apiGet<TablePage<Record<string, unknown>>>(`/proactivitee/core-applications/table?${snmpTableParams(r).toString()}`),
  })
}

export function saveDashboardElements(names: string[]) {
  const body = new FormData()
  for (const name of names) body.append('dashboard_elements[]', name)
  return apiPostForm<{ status: string; message: string }>('/proactivitee/core-applications/elements', body)
}

export function addCoreApplication(name: string) {
  return apiPost<{ status: string; message: string }>('/proactivitee/core-applications', { application_name: name })
}

export function removeCoreApplications(ids: number[]) {
  const body = new FormData()
  for (const id of ids) body.append('ids[]', String(id))
  return apiPostForm<{ status: string; message: string }>('/proactivitee/core-applications/remove', body)
}

export function coreApplicationsDashboardQuery() {
  return queryOptions({
    queryKey: ['proactivitee', 'core-apps', 'dashboard'],
    queryFn: () => apiGet<Record<string, unknown>>('/proactivitee/core-applications/dashboard'),
  })
}

/** O cadere din sertar: ce serviciu, pe ce masina, si de ce. */
export interface DownEntry {
  device?: string
  service: string
  type: 'critical' | 'warning'
  details: string[]
}

export interface AppContact {
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
}

export function applicationDownQuery(id: number) {
  return queryOptions({
    queryKey: ['proactivitee', 'apps-dashboard', 'application', id],
    queryFn: () => apiGet<{ contact: AppContact | null; entries: DownEntry[] }>(`/proactivitee/applications-dashboard/application/${id}`),
  })
}

export function deviceDownQuery(id: number) {
  return queryOptions({
    queryKey: ['proactivitee', 'apps-dashboard', 'device', id],
    queryFn: () => apiGet<{ entries: DownEntry[] }>(`/proactivitee/applications-dashboard/device/${id}`),
  })
}

/**
 * Applications Dashboard se reciteste singur la doua minute.
 *
 * Asa facea si in portal: `index-1.js` reincarca toate cele patru bucati la
 * `setInterval(..., 120000)`. E un ecran de supraveghere - sta deschis pe un
 * monitor, si o masina cazuta acum zece minute trebuie sa se vada fara ca
 * cineva sa dea F5. La mutarea in v4 intervalul s-a pierdut, si ecranul arata
 * starea din clipa in care fusese deschis.
 *
 * Aceleasi doua minute, nu mai des: citirea numara pe rand starea fiecarui
 * serviciu al fiecarei masini, iar o firma cu cateva sute de masini plateste
 * asta la fiecare trecere.
 */
export function applicationsDashboardQuery() {
  return queryOptions({
    queryKey: ['proactivitee', 'apps-dashboard'],
    queryFn: () => apiGet<Record<string, unknown>>('/proactivitee/applications-dashboard'),
    refetchInterval: 120_000,
  })
}

/** O legatura masina-aplicatie. `service_id` e cheia de stare, `device_service_id` cea de stergere. */
export interface CoreAppDeviceRow {
  device_id: number
  device_name: string
  device_service_id: number | null
  service_id: number | null
  service_name: string | null
  service_value: string | null
}

export interface CoreAppTab {
  slug: string
  label: string
  allowed: boolean
}

export function coreApplicationDevicesQuery(id: number) {
  return queryOptions({
    queryKey: ['proactivitee', 'core-apps', 'devices', id],
    queryFn: () =>
      apiGet<{ data: CoreAppDeviceRow[]; tabs: CoreAppTab[]; canWrite: boolean }>(
        `/proactivitee/core-applications/${id}/devices`
      ),
  })
}

/** Fila Settings a randului desfacut - `core_applications/settings.php`. */
export interface CoreAppSettings {
  canWrite: boolean
  technology_name: string
  icon: string
  contact: { contact_name: string; contact_email: string | null; contact_phone: string | null }
}

export function coreApplicationSettingsQuery(id: number) {
  return queryOptions({
    queryKey: ['proactivitee', 'core-apps', 'settings', id],
    queryFn: () => apiGet<CoreAppSettings>(`/proactivitee/core-applications/${id}/settings`),
  })
}

export function saveCoreApplicationSettings(id: number, fields: Record<string, string>) {
  return apiPost<{ status: string; message: string }>(`/proactivitee/core-applications/${id}/settings`, fields)
}

export function saveCoreApplicationIcon(id: number, icon: string) {
  return apiPost<{ status: string; message: string }>(`/proactivitee/core-applications/${id}/icon`, { icon })
}

/** Serviciile urmarite ale unei masini - a doua lista din Assign device. */
export function coreAppDeviceServicesQuery(deviceId: number) {
  return queryOptions({
    queryKey: ['proactivitee', 'core-apps', 'device-services', deviceId],
    queryFn: () =>
      apiGet<{ services: { device_service_id: number; service_name: string; service_value: string | null }[] }>(
        `/proactivitee/core-applications/device-services/${deviceId}`
      ),
  })
}

export function assignCoreApplicationDevice(applicationId: number, deviceId: number, services: number[]) {
  const body = new FormData()
  body.append('device', String(deviceId))
  for (const id of services) body.append('services[]', String(id))
  return apiPostForm<{ status: string; message: string }>(`/proactivitee/core-applications/${applicationId}/devices`, body)
}

export function removeCoreApplicationDevice(applicationId: number, deviceId: number, deviceServiceId: number | null) {
  const body = new FormData()
  body.append('device', String(deviceId))
  if (deviceServiceId !== null) body.append('device_service_id', String(deviceServiceId))
  return apiPostForm<{ status: string; message: string }>(`/proactivitee/core-applications/${applicationId}/devices/remove`, body)
}

/* ------------------------------------------------------------------------ *
 * ONSITE PRESENCE - Dashboards::onsite_presence + getMonitorTechs
 * ------------------------------------------------------------------------ */

/** O cutie de tehnician, cu tot ce scria `dashboards/monitor/dashboard.php` pe ea. */
export interface PresenceBox {
  id: number
  firstName: string
  lastName: string
  photo: string
  /** absent | commuting | onsite | home | out */
  state: string
  activity: string
  where: string
  opened: number
  planned: number
  firstCheckin: string
  firstLate: boolean
  arrivedLabel: string
  arrivedAt: string
  estimated: string
  estimatedLate: boolean
  rating: string
  visits: number
  proactive: number
  requests: number
}

export interface OnsitePresencePage {
  departments: { ID: number; Nume: string }[]
  selected: number[]
  techs: PresenceBox[]
  /** Secunde pana la urmatoarea improspatare, ca `setInterval` din portal. */
  refresh: number
}

export function onsitePresenceQuery(departments: number[]) {
  const q = departments.length > 0 ? `?departments=${departments.join(',')}` : ''
  return queryOptions({
    queryKey: ['dashboards', 'onsite_presence', departments],
    queryFn: () => apiGet<OnsitePresencePage>(`/dashboards/onsite_presence${q}`),
  })
}
